class Color {
    constructor(r, g, b) {
        this.set(r, g, b);
    }

    toRgb() {
        return `rgb(${Math.round(this.r)}, ${Math.round(this.g)}, ${Math.round(this.b)})`;
    }

    toHex() {
        return rgbToHex(Math.round(this.r), Math.round(this.g), Math.round(this.b));
    }

    set(r, g, b) {
        this.r = this.clamp(r);
        this.g = this.clamp(g);
        this.b = this.clamp(b);
    }

    hueRotate(angle = 0) {
        angle = angle / 180 * Math.PI;
        const sin = Math.sin(angle);
        const cos = Math.cos(angle);

        this.multiply([
            0.213 + cos * 0.787 - sin * 0.213,
            0.715 - cos * 0.715 - sin * 0.715,
            0.072 - cos * 0.072 + sin * 0.928,
            0.213 - cos * 0.213 + sin * 0.143,
            0.715 + cos * 0.285 + sin * 0.140,
            0.072 - cos * 0.072 - sin * 0.283,
            0.213 - cos * 0.213 - sin * 0.787,
            0.715 - cos * 0.715 + sin * 0.715,
            0.072 + cos * 0.928 + sin * 0.072,
        ]);
    }

    grayscale(value = 1) {
        this.multiply([
            0.2126 + 0.7874 * (1 - value),
            0.7152 - 0.7152 * (1 - value),
            0.0722 - 0.0722 * (1 - value),
            0.2126 - 0.2126 * (1 - value),
            0.7152 + 0.2848 * (1 - value),
            0.0722 - 0.0722 * (1 - value),
            0.2126 - 0.2126 * (1 - value),
            0.7152 - 0.7152 * (1 - value),
            0.0722 + 0.9278 * (1 - value),
        ]);
    }

    sepia(value = 1) {
        this.multiply([
            0.393 + 0.607 * (1 - value),
            0.769 - 0.769 * (1 - value),
            0.189 - 0.189 * (1 - value),
            0.349 - 0.349 * (1 - value),
            0.686 + 0.314 * (1 - value),
            0.168 - 0.168 * (1 - value),
            0.272 - 0.272 * (1 - value),
            0.534 - 0.534 * (1 - value),
            0.131 + 0.869 * (1 - value),
        ]);
    }

    saturate(value = 1) {
        this.multiply([
            0.213 + 0.787 * value,
            0.715 - 0.715 * value,
            0.072 - 0.072 * value,
            0.213 - 0.213 * value,
            0.715 + 0.285 * value,
            0.072 - 0.072 * value,
            0.213 - 0.213 * value,
            0.715 - 0.715 * value,
            0.072 + 0.928 * value,
        ]);
    }

    multiply(matrix) {
        const newR = this.clamp(this.r * matrix[0] + this.g * matrix[1] + this.b * matrix[2]);
        const newG = this.clamp(this.r * matrix[3] + this.g * matrix[4] + this.b * matrix[5]);
        const newB = this.clamp(this.r * matrix[6] + this.g * matrix[7] + this.b * matrix[8]);
        this.r = newR;
        this.g = newG;
        this.b = newB;
    }

    brightness(value = 1) {
        this.linear(value);
    }
    contrast(value = 1) {
        this.linear(value, -(0.5 * value) + 0.5);
    }

    linear(slope = 1, intercept = 0) {
        this.r = this.clamp(this.r * slope + intercept * 255);
        this.g = this.clamp(this.g * slope + intercept * 255);
        this.b = this.clamp(this.b * slope + intercept * 255);
    }

    invert(value = 1) {
        this.r = this.clamp((value + this.r / 255 * (1 - 2 * value)) * 255);
        this.g = this.clamp((value + this.g / 255 * (1 - 2 * value)) * 255);
        this.b = this.clamp((value + this.b / 255 * (1 - 2 * value)) * 255);
    }

    hsl() {
        // Code taken from https://stackoverflow.com/a/9493060/2688027, licensed under CC BY-SA.
        const r = this.r / 255;
        const g = this.g / 255;
        const b = this.b / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;

                case g:
                    h = (b - r) / d + 2;
                    break;

                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }

        return {
            h: h * 100,
            s: s * 100,
            l: l * 100,
        };
    }

    clamp(value) {
        if (value > 255) {
            value = 255;
        } else if (value < 0) {
            value = 0;
        }
        return value;
    }
}

class Solver {
    constructor(target, baseColor) {
        this.target = target;
        this.targetHSL = target.hsl();
        this.reusedColor = new Color(0, 0, 0);
    }

    solve() {
        const result = this.solveNarrow(this.solveWide());
        return {
            values: result.values,
            loss: result.loss,
            filter: this.css(result.values),
            filterRaw: this.raw(result.values),
        };
    }

    solveWide() {
        const A = 5;
        const c = 15;
        const a = [60, 180, 18000, 600, 1.2, 1.2];

        let best = { loss: Infinity };
        for (let i = 0; best.loss > 25 && i < 3; i++) {
            const initial = [50, 20, 3750, 50, 100, 100];
            const result = this.spsa(A, a, c, initial, 1000);
            if (result.loss < best.loss) {
                best = result;
            }
        }
        return best;
    }

    solveNarrow(wide) {
        const A = wide.loss;
        const c = 2;
        const A1 = A + 1;
        const a = [0.25 * A1, 0.25 * A1, A1, 0.25 * A1, 0.2 * A1, 0.2 * A1];
        return this.spsa(A, a, c, wide.values, 500);
    }

    spsa(A, a, c, values, iters) {
        const alpha = 1;
        const gamma = 0.16666666666666666;

        let best = null;
        let bestLoss = Infinity;
        const deltas = new Array(6);
        const highArgs = new Array(6);
        const lowArgs = new Array(6);

        for (let k = 0; k < iters; k++) {
            const ck = c / Math.pow(k + 1, gamma);
            for (let i = 0; i < 6; i++) {
                deltas[i] = Math.random() > 0.5 ? 1 : -1;
                highArgs[i] = values[i] + ck * deltas[i];
                lowArgs[i] = values[i] - ck * deltas[i];
            }

            const lossDiff = this.loss(highArgs) - this.loss(lowArgs);
            for (let i = 0; i < 6; i++) {
                const g = lossDiff / (2 * ck) * deltas[i];
                const ak = a[i] / Math.pow(A + k + 1, alpha);
                values[i] = fix(values[i] - ak * g, i);
            }

            const loss = this.loss(values);
            if (loss < bestLoss) {
                best = values.slice(0);
                bestLoss = loss;
            }
        }
        return { values: best, loss: bestLoss };

        function fix(value, idx) {
            let max = 100;
            if (idx === 2 /* saturate */) {
                max = 7500;
            } else if (idx === 4 /* brightness */ || idx === 5 /* contrast */) {
                max = 200;
            }

            if (idx === 3 /* hue-rotate */) {
                if (value > max) {
                    value %= max;
                } else if (value < 0) {
                    value = max + value % max;
                }
            } else if (value < 0) {
                value = 0;
            } else if (value > max) {
                value = max;
            }
            return value;
        }
    }

    loss(filters) {
        // Argument is array of percentages.
        const color = this.reusedColor;
        color.set(0, 0, 0);

        color.invert(filters[0] / 100);
        color.sepia(filters[1] / 100);
        color.saturate(filters[2] / 100);
        color.hueRotate(filters[3] * 3.6);
        color.brightness(filters[4] / 100);
        color.contrast(filters[5] / 100);

        const colorHSL = color.hsl();
        return (
            Math.abs(color.r - this.target.r) +
            Math.abs(color.g - this.target.g) +
            Math.abs(color.b - this.target.b) +
            Math.abs(colorHSL.h - this.targetHSL.h) +
            Math.abs(colorHSL.s - this.targetHSL.s) +
            Math.abs(colorHSL.l - this.targetHSL.l)
        );
    }

    raw(filters) {
        function fmt(idx, multiplier = 1) {
            return Math.round(filters[idx] * multiplier);
        }
        return `brightness(0) saturate(100%) invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%)`;
    }

    css(filters) {
        function fmt(idx, multiplier = 1) {
            return Math.round(filters[idx] * multiplier);
        }
        return `filter: brightness(0) saturate(100%) invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%);`;
    }
}

function hexToRgb(hexColor) {
    // Remove hash character, if present
    hexColor = hexColor.replace('#', '');

    // Convert 3-digit hex to 6-digit hex
    if (hexColor.length === 3) {
        hexColor = hexColor
            .split('')
            .map((char) => char + char)
            .join('');
    }

    // Convert hex to RGB
    const red = parseInt(hexColor.substring(0, 2), 16);
    const green = parseInt(hexColor.substring(2, 4), 16);
    const blue = parseInt(hexColor.substring(4, 6), 16);

    return { red, green, blue };
}


const HSLStringToHex = function (hslString) {
    if (hslString[0] != 'h') {
        return hslString;
    }
    // Remove 'hsl(' and ')' from the input string
    var hslValues = hslString.substring(4, hslString.length - 1).split(',');

    // Extract H, S, and L values
    var hue = parseInt(hslValues[0].trim(), 10);
    var saturation = parseInt(hslValues[1].trim(), 10);
    var lightness = parseInt(hslValues[2].trim(), 10);

    let [r, g, b] = HSLToRGB(hue, saturation, lightness);
    return RGBToHEX(r, g, b);
}

const componentToHex = (c) => {
    // Max is 255 and min is 0
    c = Math.min(Math.max(0, c), 255);
    c = Math.round(c);
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
}

const RGBToHEX = (r, g, b) => {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

const HSLToRGB = (h, s, l) => {
    var r, g, b;
    h /= 360;
    s /= 100;
    l /= 100;

    if (s == 0) {
        r = g = b = l; // achromatic
    } else {
        function hue2rgb(p, q, t) {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        }

        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
}

const RGBToHSL = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const l = Math.max(r, g, b);
    const s = l - Math.min(r, g, b);
    const h = s
        ? l === r
            ? (g - b) / s
            : l === g
                ? 2 + (b - r) / s
                : 4 + (r - g) / s
        : 0;
    return [
        60 * h < 0 ? 60 * h + 360 : 60 * h,
        100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
        (100 * (2 * l - s)) / 2,
    ];
};

function HEXtoHSL(hex) {
    const { red, green, blue } = hexToRgb(hex);
    return RGBToHSL(red, green, blue);
}

function HSLtoHEX(hsl) {
    const [h, s, l] = hsl;
    const [r, g, b] = HSLToRGB(h, s, l);
    return RGBToHEX(r, g, b);
}

function colorToHSL(color) {
    let hsl = null
    if (color[0] == 'r') {
        let rgb = color.replace('rgb(', '').replace(')', '').split(',').map((v) => parseFloat(v))
        hsl = RGBToHSL(rgb[0], rgb[1], rgb[2])
    } else {
        hsl = HEXtoHSL(color)
    }
    return hsl
}
function HUERotate(color, angle) {
    let hsl = colorToHSL(color)
    hsl[0] = (hsl[0] + angle) % 360
    return HSLtoHEX(hsl)
}

function TransformColor(color, angle, saturation) {
    let hsl = colorToHSL(color)
    hsl[0] = (hsl[0] + angle) % 360
    if (saturation) {
        hsl[1] = Math.min(100, Math.max(0, hsl[1]*saturation))
    }
    return HSLtoHEX(hsl)
}

function setLuminance(color, luminance) {
    let [h, s, l] = [0, 0, 0];
    if (color[0] == '#') {
        const { red, green, blue } = hexToRgb(color);
        [h, s, l] = RGBToHSL(red, green, blue);
    } else if (color[0] == 'h') {
        [h, s, l] = color.replace('hsl(', '').replace(')', '').split(',').map((v) => parseFloat(v));
    }
    const [r, g, b] = HSLToRGB(h, s, luminance);
    return RGBToHEX(r, g, b);
}

function mergeWithBlack(color, weight) {
    let colorRGB = hexToRgb(color)
    color = [colorRGB.red, colorRGB.green, colorRGB.blue]
    const colorDec = color.map(value => value / 255);
    const blackDec = [0, 0, 0];
    const mergedColor = colorDec.map((value, index) => (value * (1 - weight)) + (blackDec[index] * weight));
    const mergedColorRgb = mergedColor.map(value => Math.round(value * 255));

    return RGBToHEX(mergedColorRgb[0], mergedColorRgb[1], mergedColorRgb[2]);
}

function mergeWithWhite(color, weight) {
    let colorRGB = hexToRgb(color)
    color = [colorRGB.red, colorRGB.green, colorRGB.blue]
    const colorDec = color.map(value => value / 255);
    const whiteDec = [1, 1, 1];
    const mergedColor = colorDec.map((value, index) => (value * (1 - weight)) + (whiteDec[index] * weight));
    const mergedColorRgb = mergedColor.map(value => Math.round(value * 255));

    return RGBToHEX(mergedColorRgb[0], mergedColorRgb[1], mergedColorRgb[2]);
}

function makeColorDarker(color, amount) {
    const { red, green, blue } = hexToRgb(color);
    const [h, s, l] = RGBToHSL(red, green, blue);
    const [r, g, b] = HSLToRGB(h, s, l > amount ? l - amount : 5);
    return RGBToHEX(r, g, b);
}

function makeColorLighter(color, amount) {
    return makeColorDarker(color, -amount);
}

function adjustLightness(color, percent=0.0) {
    const { red, green, blue } = hexToRgb(color);
    const [h, s, l] = RGBToHSL(red, green, blue);
    const [r, g, b] = HSLToRGB(h, s, l + percent);
    return RGBToHEX(r, g, b);
}

function improveLuminance(color, targetLuminance) {
    const { red, green, blue } = hexToRgb(color);
    [h, s, l] = RGBToHSL(red, green, blue);

    if (l > 50) {
        return null
    }
    return setLuminance(color, targetLuminance);
}

function getHEXRelativeLuminance(hexColor) {
    const { red, green, blue } = hexToRgb(hexColor);
    return getRelativeLuminance({ red, green, blue });
}

function getRelativeLuminance(rgbColor) {
    const { red, green, blue } = rgbColor;

    const sRGB = [red, green, blue].map((value) => {
        const sRGBValue = value / 255;
        return sRGBValue <= 0.03928 ? sRGBValue / 12.92 : Math.pow((sRGBValue + 0.055) / 1.055, 2.4);
    });

    const relativeLuminance = 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];

    return relativeLuminance;
}

function getContrastRatio(hexColor1, hexColor2) {
    // Convert hex colors to RGB
    const color1 = hexToRgb(hexColor1);
    const color2 = hexToRgb(hexColor2);

    // Calculate relative luminance
    const luminance1 = getRelativeLuminance(color1);
    const luminance2 = getRelativeLuminance(color2);

    // Calculate contrast ratio
    const contrastRatio = (Math.max(luminance1, luminance2) + 0.05) / (Math.min(luminance1, luminance2) + 0.05);

    return contrastRatio;
}

function getContrastColor(color, minContrast = 4, forceLighter = null) {
    let colorRGB = hexToRgb(color)
    let hsl = RGBToHSL(colorRGB.red, colorRGB.green, colorRGB.blue)

    // TASK [HP] - Make it fail safe
    let shouldMakeLighter = forceLighter != null ? forceLighter : hsl[2] < 50

    // Loop until we get a color with enough contrast by making it lighter or darker
    let contrast = 0
    let updatedRGB = []
    let maxTries = 100
    while (contrast < minContrast || (hsl[2] < 1 || hsl[2] >= 99)) {
        if (shouldMakeLighter) {
            hsl[2] += 1
        } else {
            hsl[2] -= 1
        }
        updatedRGB = HSLToRGB(hsl[0], hsl[1], hsl[2])
        contrast = getContrastRatio(color, RGBToHEX(updatedRGB[0], updatedRGB[1], updatedRGB[2]))
        if (maxTries-- < 0) {
            break
        }
    }
    return RGBToHEX(updatedRGB[0], updatedRGB[1], updatedRGB[2])
}

function improveContrast(main, text, minContrast = 4, forceLighter = null) {
    let contrast = getContrastRatio(main, text)
    if (contrast >= minContrast) {
        return {
            main,
            text
        }
    }
    let contrastColor = getContrastColor(main, minContrast, forceLighter)
    return {
        main,
        text: contrastColor
    }
}

function improvePallete(pallete, type = 'dark') {
    let previousPallete = JSON.parse(JSON.stringify(pallete))

    let [h, s, l] = HEXtoHSL(pallete.main)
    let factorMerge = 0.4
    if (l > 80) {
        factorMerge = 0.5
    }

    let mainColor = null
    if (type == 'dark') {
        mainColor = mergeWithBlack(pallete.main, factorMerge)
    } else {
        mainColor = mergeWithWhite(pallete.main, factorMerge)
    }
    let forceLighter = null
    if (type == 'white') {
        forceLighter = false
    }
    let improvedTextColor = improveContrast(mainColor, pallete.text, 5, forceLighter)
    if (improvedTextColor) {
        pallete.text = improvedTextColor.text
    }
    let improvedHighlightColor = improveContrast(mainColor, pallete.highlight, 6, forceLighter)
    if (improvedHighlightColor) {
        pallete.highlight = improvedHighlightColor.text
    }

    return pallete
}


const normalizeToHEX = function(color) {
    try {
        if (color[0] == '#') {
            return color;
        } else if (color[0] == 'h') {
            const [h, s, l] = color.replace('hsl(', '').replace(')', '').split(',').map((v) => parseFloat(v));
            const [r, g, b] = HSLToRGB(h, s, l);
            return RGBToHEX(r, g, b);
        } else if (color.startsWith('rgb')) {
            const [r, g, b] = color.replace('rgb(', '').replace(')', '').split(',').map((v) => parseFloat(v));
            return RGBToHEX(r, g, b);
        }
    } catch (e) {
        console.log(e)
    }
    return color;
}
